import {
  BusinessCenter,
  CalendarToday,
  Description,
  Info,
  MonetizationOn,
  Person,
} from '@mui/icons-material';
import { Close } from '@mui/icons-material';
import AssignmentIcon from '@mui/icons-material/Assignment';
import { Box, Grid } from '@mui/material';
import Dialog from '@mui/material/Dialog';
import IconButton from '@mui/material/IconButton';
import { formatDate, formatMoney } from 'lib/functions/utils';
import { PurchaseOrderItem } from 'lib/types/models';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { showBusinessNetwork } from 'services/redux/businessNetworkSlice';
import { hidePurchaseOrderModal } from 'services/redux/purchaseOrderModalSlice';
import { RootState } from 'services/redux/store';

import InfoCard from '../TenderModal/Components/InfoCard';
import SectionCard from '../TenderModal/Components/SectionCard';
import CostBreakdown from './Components/CostBreakdown';
import POHeader from './Components/POHeader';
import POItemsSection from './Components/POItemsSection';

const PurchaseOrderModals: React.FC = () => {
  const dispatch = useDispatch();
  const { open, purchaseOrder } = useSelector(
    (state: RootState) => state.purchaseOrderModal,
  );

  const handleClose = () => {
    dispatch(hidePurchaseOrderModal());
  };

  if (!purchaseOrder) return null;

  const items: PurchaseOrderItem[] = purchaseOrder.items || [];

  const safeDate = (value?: string | null) =>
    value ? formatDate(new Date(value), 'dd-mm-yyyy') : 'No disponible';

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          maxHeight: '90vh',
          borderRadius: 2,
          boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
        },
      }}
    >
      <IconButton
        aria-label="close"
        onClick={handleClose}
        sx={{
          position: 'absolute',
          top: 12,
          right: 12,
          zIndex: 1,
          backgroundColor: 'rgba(0,0,0,0.04)',
          '&:hover': { backgroundColor: 'rgba(0,0,0,0.08)' },
        }}
      >
        <Close />
      </IconButton>

      <Box sx={{ p: 2.5, pt: 3 }}>
        <POHeader
          name={purchaseOrder.name ?? 'Orden de compra'}
          externalCode={purchaseOrder.externalCode}
          statusCode={purchaseOrder.statusCode}
        />

        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <SectionCard title="Información General">
              <Grid container spacing={1.5}>
                <InfoCard
                  icon={<Description />}
                  label="Descripción"
                  valueText={purchaseOrder.description || 'No disponible'}
                  color={'brown'}
                  clampLines={3}
                  showTooltip
                />
                <InfoCard
                  icon={<BusinessCenter />}
                  label="Tipo"
                  valueText={purchaseOrder.type}
                  color={'primary.main'}
                />
                <InfoCard
                  icon={<MonetizationOn />}
                  label="Moneda"
                  valueText={purchaseOrder.currency}
                  color={'success.main'}
                />
                <InfoCard
                  icon={<AssignmentIcon />}
                  label="Código de Licitación"
                  valueText={purchaseOrder.tenderCode || 'No disponible'}
                  color={'error.main'}
                />
                <InfoCard
                  icon={<CalendarToday />}
                  label="Fecha de Creación"
                  valueText={safeDate(purchaseOrder.creationDate)}
                  color={'info.main'}
                />
                <InfoCard
                  icon={<CalendarToday />}
                  label="Fecha de Despacho"
                  valueText={safeDate(purchaseOrder.shippingDate)}
                  color={'warning.main'}
                />
                <InfoCard
                  icon={<CalendarToday />}
                  label="Fecha de Aceptación"
                  valueText={safeDate(purchaseOrder.acceptanceDate)}
                  color={'success.main'}
                />
                <InfoCard
                  icon={<CalendarToday />}
                  label="Fecha de Cancelación"
                  valueText={safeDate(purchaseOrder.cancellationDate)}
                  color={'error.main'}
                />
              </Grid>
            </SectionCard>

            <SectionCard
              title="Comprador"
              titleEndAdornment={
                purchaseOrder.buyer ? (
                  <IconButton
                    size="small"
                    aria-label="Ver detalles del comprador"
                    onClick={() =>
                      dispatch(
                        showBusinessNetwork({
                          id: purchaseOrder.buyer?.identifier,
                          name: purchaseOrder.buyer?.name,
                          path: 'public-market',
                        }),
                      )
                    }
                  >
                    <Info fontSize="small" />
                  </IconButton>
                ) : null
              }
            >
              <Grid container spacing={1.5}>
                <InfoCard
                  icon={<Person />}
                  label="Nombre"
                  valueText={purchaseOrder.buyer?.name || 'No disponible'}
                  color={'primary.main'}
                />
                <InfoCard
                  icon={<Person />}
                  label="Identificador"
                  valueText={purchaseOrder.buyer?.identifier || 'No disponible'}
                  color={'info.main'}
                />
                <InfoCard
                  icon={<Person />}
                  label="Unidad"
                  valueText={purchaseOrder.buyerUnit?.name || 'No disponible'}
                  color={'warning.main'}
                />
              </Grid>
            </SectionCard>

            <SectionCard
              title="Proveedor"
              titleEndAdornment={
                purchaseOrder.supplier ? (
                  <IconButton
                    size="small"
                    aria-label="Ver detalles del proveedor"
                    onClick={() =>
                      dispatch(
                        showBusinessNetwork({
                          id: purchaseOrder.supplier?.identifier,
                          name: purchaseOrder.supplier?.name,
                        }),
                      )
                    }
                  >
                    <Info fontSize="small" />
                  </IconButton>
                ) : null
              }
            >
              <Grid container spacing={1.5}>
                <InfoCard
                  icon={<Person />}
                  label="Nombre"
                  valueText={purchaseOrder.supplier?.name || 'No disponible'}
                  color={'primary.main'}
                />
                <InfoCard
                  icon={<Person />}
                  label="Identificador"
                  valueText={
                    purchaseOrder.supplier?.identifier || 'No disponible'
                  }
                  color={'info.main'}
                />
                <InfoCard
                  icon={<Person />}
                  label="Unidad"
                  valueText={
                    purchaseOrder.supplierUnit?.name || 'No disponible'
                  }
                  color={'warning.main'}
                />
              </Grid>
            </SectionCard>
          </Grid>

          <Grid item xs={12} md={6}>
            <POItemsSection items={items} />

            <SectionCard
              title="Resumen y Calculadora de Montos"
              chipLabel="Calculadora"
            >
              <CostBreakdown
                currency={purchaseOrder.currency}
                netTotal={purchaseOrder.netTotal ?? undefined}
                ivaPercentage={purchaseOrder.ivaPercentage ?? undefined}
                taxes={purchaseOrder.taxes ?? undefined}
                total={purchaseOrder.total ?? undefined}
                items={items}
                formatter={formatMoney}
              />
            </SectionCard>
          </Grid>
        </Grid>
      </Box>
    </Dialog>
  );
};

export default PurchaseOrderModals;
